import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mainApi } from '@/lib/api';
import { QUERY_KEYS, StoreType } from '@/lib/constants';
import { stanNotify } from '@/lib/stanNotify';
import { PageStatus } from '@/types/store';

export function useDuplicateFunnelPage(storeId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (pageId: number) => {
            const { data: rawPageData } = await mainApi.get(`v1/stores/${storeId}/pages/${pageId}`);
            const page = (rawPageData.page || rawPageData) as Record<string, unknown>;
            const pData = page.data as Record<string, unknown>;

            const productData = { ...(pData.product as Record<string, unknown>) };
            delete productData.id;

            if (page.type === 'course' && productData.course) {
                const course = productData.course as Record<string, unknown>;
                (course.modules as Record<string, unknown>[] | undefined)?.forEach((mod: Record<string, unknown>) => {
                    delete mod.module_id;
                    (mod.lessons as Record<string, unknown>[] | undefined)?.forEach(
                        (lesson: Record<string, unknown>) => {
                            delete lesson.lesson_id;
                        }
                    );
                });
                if ('toc' in course) delete course.toc;
            }

            await mainApi.put(`v1/stores/${storeId}/pages/draft`, {
                ...pData,
                product: productData,
                order: 999,
                // Duplicated pages land as drafts so the creator can review before publishing.
                status: PageStatus.DRAFT,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FUNNELS, 'detail', storeId] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA, StoreType.FUNNEL] });
            stanNotify({ type: 'success', title: 'Page duplicated!', duration: 2000 });
        },
        onError: () => {
            stanNotify({
                type: 'error',
                title: 'Oops!',
                text: 'Failed to duplicate page. Please try again.',
                duration: 2000,
            });
        },
    });
}
